import { Box, Button, Tooltip, Typography } from '@mui/material';
import {
  faInfoCircle,
  faPencil,
  faPlus,
  faTimes,
  faWarning
} from '@fortawesome/pro-light-svg-icons';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { mutations, queries } from 'RestAPIs/rapini';
import {
  DutyAskRoundSettingsResponse,
  AskRoundSettingsResponse
} from 'generated/index';
import useGetAIMatchSettings from './hooks/useGetAIMatchSettings';
import {
  AIMatchKey,
  AIMatchSettingsType,
  AdminSettings,
  Setting
} from 'components/types';
import ControlledNumericField from 'components/shared/ControlledFields/ControlledNumericField';
import schema from './schema';
import {
  ControlledAutoComplete,
  ControlledCheckbox
} from 'components/shared/ControlledFields';
import { getTimeSlotOptions, getValuesByAIMatchSettingsType } from './utils';
import useUpdateAIMatchSettings from './hooks/useUpdateAIMatchSettings';
import { MouseEvent, useEffect, useState } from 'react';
import FontAwesomeSvgIcon from 'components/shared/FontAwesomeSvgIcon';
import {
  showErrorSnackbar,
  showSuccessSnackbar
} from 'components/shared/Snackbar';
import RoundModal from './components/RoundModal/RoundModal';
import { ORANGE, RED } from 'constants/colors';
import PhaseMetaData from './components/PhaseMetaData';
import LoadingOverlay from 'components/shared/LoadingOverlay';

type AIMatchForm = {
  [AIMatchSettingsType.TIMES]: { label: string; value: string }[];
  [AIMatchSettingsType.BEFORE_START_LIMIT]: number;
  [AIMatchSettingsType.ROUND_MINUTES_BEFORE_START_LIMIT]: number;
  [AIMatchSettingsType.FLEX_ENABLED]: boolean;
  [AIMatchSettingsType.NON_FLEX_ENABLED]: boolean;
  rounds: AskRoundSettingsResponse[];
};

const defaultSettingValues: AIMatchForm = {
  [AIMatchSettingsType.TIMES]: [],
  [AIMatchSettingsType.BEFORE_START_LIMIT]: 0,
  [AIMatchSettingsType.ROUND_MINUTES_BEFORE_START_LIMIT]: 0,
  [AIMatchSettingsType.FLEX_ENABLED]: false,
  [AIMatchSettingsType.NON_FLEX_ENABLED]: false,
  rounds: []
};

export const defaultRoundValues: AskRoundSettingsResponse = {
  minutesBeforeStart: 0,
  minimalMinutesOfAdvantageBeforeNext: 0,
  includeOwn: false,
  includeShared: false,
  includePreference1: false,
  includePreference2: false,
  includeFlexWithPreference1: false,
  includeFlexWithPreference2: false,
  includeFlex: false,
  includeDeployableAsFlexWithPreference1: false,
  includeDeployableAsFlexWithPreference2: false,
  includeDeployableAsFlex: false,
  overlapMargin: 0,
  responseActionType: 'CONNECT'
};

const AIMatchSettings = () => {
  const { t } = useTranslation('common', { keyPrefix: 'aiMatchSettings' });

  const methods = useForm<AIMatchForm>({
    defaultValues: schema.cast(defaultSettingValues),
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const {
    control,
    formState,
    handleSubmit,
    setValue,
    getValues,
    watch,
    reset
  } = methods;

  const [currentRound, setCurrentRound] = useState<
    { index: number; round: AskRoundSettingsResponse } | null | undefined
  >(null);

  const { data: aiMatchSettings, isLoading: isLoadingAIMatchSettings } =
    useGetAIMatchSettings();
  const { mutateAsync: updateAIMatchSettings } = useUpdateAIMatchSettings();

  const { isLoading: isLoadingRoundSettings, data: roundSettings } =
    queries.useGetDutyAskRoundSettings();
  const {
    isLoading: isLoadingUpdateRoundSettings,
    mutateAsync: updateRoundSettings
  } = mutations.useUpdateDutyAskRoundSettings();

  const isLoading =
    isLoadingAIMatchSettings ||
    isLoadingRoundSettings ||
    isLoadingUpdateRoundSettings;

  const settings: Setting[] = aiMatchSettings?.settings || [];
  const timeSlots = getTimeSlotOptions() || [];

  const getSettingByType = (type: AIMatchSettingsType) => {
    switch (type) {
      case AIMatchSettingsType.TIMES:
        return (
          <Box display="flex" gap={1} position="relative">
            <ControlledAutoComplete
              name={AIMatchSettingsType.TIMES}
              label={t('times')}
              control={control}
              disabled={isLoading}
              optionLabelProp="label"
              idProperty="value"
              options={timeSlots}
              variant="outlined"
              multiple
            />
            <Tooltip title={t('timesTooltip')} arrow>
              <FontAwesomeSvgIcon
                icon={faInfoCircle}
                sx={{
                  mt: 1,
                  position: 'absolute',
                  right: '-40px'
                }}
              />
            </Tooltip>
          </Box>
        );

      case AIMatchSettingsType.BEFORE_START_LIMIT:
        return (
          <Box display="flex" mt={2} gap={1} position="relative">
            <ControlledNumericField
              name={AIMatchSettingsType.BEFORE_START_LIMIT}
              label={t('beforeStartLimit')}
              control={control}
              disabled={isLoading}
            />
            <Tooltip title={t('beforeStartLimitTooltip')} arrow>
              <FontAwesomeSvgIcon
                icon={faInfoCircle}
                sx={{
                  mt: 1,
                  position: 'absolute',
                  right: '-40px'
                }}
              />
            </Tooltip>
          </Box>
        );
      case AIMatchSettingsType.ROUND_MINUTES_BEFORE_START_LIMIT:
        return (
          <Box display="flex" mt={2} gap={1} position="relative">
            <ControlledNumericField
              name={AIMatchSettingsType.ROUND_MINUTES_BEFORE_START_LIMIT}
              label={t('roundBeforeStartLimit')}
              control={control}
              disabled={isLoading}
            />
            <Tooltip title={t('roundBeforeStartLimitTooltip')} arrow>
              <FontAwesomeSvgIcon
                icon={faInfoCircle}
                sx={{
                  mt: 1,
                  position: 'absolute',
                  right: '-40px'
                }}
              />
            </Tooltip>
          </Box>
        );
      case AIMatchSettingsType.FLEX_ENABLED:
        return (
          <ControlledCheckbox
            name={AIMatchSettingsType.FLEX_ENABLED}
            label={t('flexEnabled')}
            control={control}
            disabled={isLoading}
          />
        );
      case AIMatchSettingsType.NON_FLEX_ENABLED:
        return (
          <ControlledCheckbox
            name={AIMatchSettingsType.NON_FLEX_ENABLED}
            label={t('nonFlexEnabled')}
            control={control}
            disabled={isLoading}
          />
        );
      default:
        return null;
    }
  };

  const onSubmit = async (formData: AIMatchForm) => {
    const matchSettingsPayload = {
      ...aiMatchSettings,
      settings: [
        ...(settings || []).map((setting) => {
          if (setting.key === `${AIMatchKey}.${AIMatchSettingsType.TIMES}`) {
            return {
              ...setting,
              value: formData[AIMatchSettingsType.TIMES]
                .map(({ value }) => value)
                .join()
            };
          }

          if (
            setting.key ===
            `${AIMatchKey}.${AIMatchSettingsType.BEFORE_START_LIMIT}`
          ) {
            return {
              ...setting,
              value: formData[AIMatchSettingsType.BEFORE_START_LIMIT].toString()
            };
          }

          return setting;
        })
      ]
    } as AdminSettings;

    const roundSettingsPayload = {
      [AIMatchSettingsType.ROUND_MINUTES_BEFORE_START_LIMIT]:
        formData[AIMatchSettingsType.ROUND_MINUTES_BEFORE_START_LIMIT] ||
        roundSettings?.minutesBeforeStartLimit,
      [AIMatchSettingsType.FLEX_ENABLED]:
        formData[AIMatchSettingsType.FLEX_ENABLED] ||
        roundSettings?.flexEnabled,
      [AIMatchSettingsType.NON_FLEX_ENABLED]:
        formData[AIMatchSettingsType.NON_FLEX_ENABLED] ||
        roundSettings?.nonFlexEnabled,
      rounds: formData.rounds || roundSettings?.rounds || []
    } as DutyAskRoundSettingsResponse;

    try {
      await updateAIMatchSettings(matchSettingsPayload);
      await updateRoundSettings(roundSettingsPayload);

      reset(formData);
      showSuccessSnackbar();
    } catch (err) {
      showErrorSnackbar();
    }
  };

  const addRound = (event: MouseEvent<HTMLButtonElement>, index: number) => {
    event.preventDefault();
    setValue('rounds', [...getValues().rounds, defaultRoundValues]);

    const newRound = getValues().rounds[getValues().rounds.length - 1];
    setCurrentRound({ index, round: newRound });
  };

  const updateRound = (index: number, round: AskRoundSettingsResponse) => {
    setValue(
      'rounds',
      getValues().rounds.map((r, i) => (i === index ? round : r)),
      { shouldDirty: true }
    );
    setCurrentRound(null);
  };

  const editRound = (
    event: MouseEvent<HTMLButtonElement>,
    index: number,
    round: AskRoundSettingsResponse
  ) => {
    event.preventDefault();
    setCurrentRound({ index, round });
  };

  const removeRound = (event: MouseEvent<HTMLButtonElement>, index: number) => {
    event.preventDefault();
    setValue(
      'rounds',
      getValues().rounds.filter((_, i) => i !== index),
      { shouldDirty: true }
    );
  };

  useEffect(() => {
    if (aiMatchSettings) {
      // Set existing values to form
      methods.reset(
        schema.cast({
          // Notification settings
          [AIMatchSettingsType.BEFORE_START_LIMIT]: +(
            getValuesByAIMatchSettingsType(
              AIMatchSettingsType.BEFORE_START_LIMIT,
              settings
            )?.value ||
            defaultSettingValues[AIMatchSettingsType.BEFORE_START_LIMIT]
          ),
          // Map times settings to label/value object pairs
          [AIMatchSettingsType.TIMES]: getValuesByAIMatchSettingsType(
            AIMatchSettingsType.TIMES,
            settings
          )
            ?.value.split(',')
            .map((value) => ({ label: value, value })),

          // Round settings
          [AIMatchSettingsType.ROUND_MINUTES_BEFORE_START_LIMIT]:
            roundSettings?.minutesBeforeStartLimit ||
            defaultSettingValues[
              AIMatchSettingsType.ROUND_MINUTES_BEFORE_START_LIMIT
            ],
          [AIMatchSettingsType.FLEX_ENABLED]:
            roundSettings?.flexEnabled ||
            defaultSettingValues[AIMatchSettingsType.FLEX_ENABLED],
          [AIMatchSettingsType.NON_FLEX_ENABLED]:
            roundSettings?.nonFlexEnabled ||
            defaultSettingValues[AIMatchSettingsType.NON_FLEX_ENABLED],
          rounds: roundSettings?.rounds || []
        })
      );
    }
  }, [aiMatchSettings, roundSettings]);

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <h2>{t('title')}</h2>

          {/* Round settings */}
          <Box mt={2} maxWidth="1200px" minWidth="75%">
            <Typography variant="body1">{t('rounds')}</Typography>

            <Box display="flex" flexDirection="column" mt={2}>
              {getSettingByType(AIMatchSettingsType.FLEX_ENABLED)}
              {getSettingByType(AIMatchSettingsType.NON_FLEX_ENABLED)}
            </Box>

            {getSettingByType(
              AIMatchSettingsType.ROUND_MINUTES_BEFORE_START_LIMIT
            )}

            <Box display="flex" flexDirection="column" gap={2} mt={2}>
              {watch('rounds').map(
                (round: AskRoundSettingsResponse, index: number) => {
                  const enabledOptions: string[] = Object.entries(round)
                    .filter(([, value]) => value && typeof value === 'boolean')
                    .map(([key]) => t(`roundType.${key}`));
                  return (
                    <Box
                      key={index}
                      display="flex"
                      rowGap={2}
                      width="100%"
                      position="relative"
                    >
                      <Button
                        type="button"
                        variant="outlined"
                        color="dark"
                        fullWidth
                        onClick={(event) => editRound(event, index, round)}
                        endIcon={<FontAwesomeSvgIcon icon={faPencil} />}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <Box
                          display="flex"
                          flexDirection="column"
                          textAlign="left"
                          mr={3}
                        >
                          <Typography>
                            {t('roundNr', { number: index + 1 })}
                          </Typography>
                          {/* // TODO: AI Match x uur voor de start van de dienst */}

                          <PhaseMetaData round={round} />
                        </Box>

                        <Typography
                          variant="caption"
                          color="textSecondary"
                          sx={{
                            mr: 3,
                            fontWeight: 'regular',
                            fontSize: '14px',
                            textTransform: 'none'
                          }}
                        >
                          {enabledOptions.join(', ')}
                        </Typography>
                      </Button>

                      <Button
                        type="button"
                        variant="text"
                        sx={{
                          position: 'absolute',
                          top: 'calc(50% - 20px)',
                          right: '-45px',
                          minWidth: 'initial',
                          width: '30px',
                          ml: 1,
                          color: RED
                        }}
                        onClick={(event) => removeRound(event, index)}
                      >
                        <FontAwesomeSvgIcon icon={faTimes} />
                      </Button>
                    </Box>
                  );
                }
              )}

              <Button
                type="button"
                variant="contained"
                color="dark"
                size="large"
                sx={{
                  maxWidth: '200px',
                  alignSelf: 'flex-end'
                }}
                onClick={(event: MouseEvent<HTMLButtonElement>) =>
                  addRound(event, getValues().rounds.length)
                }
                startIcon={<FontAwesomeSvgIcon icon={faPlus} />}
              >
                {t('addRound')}
              </Button>
            </Box>
          </Box>

          {/* Notification settings */}
          <Box mt={2} maxWidth="1200px" minWidth="75%">
            <Typography variant="body1">{t('notifications')}</Typography>

            {settings.map((setting) => (
              <Box key={setting.key} mt={3}>
                {getSettingByType(
                  setting.key.split('.')[1] as AIMatchSettingsType
                )}
              </Box>
            ))}
          </Box>

          <Box display="flex" justifyContent="flex-end" alignItems="center">
            {formState.isDirty && (
              <>
                <FontAwesomeSvgIcon
                  icon={faWarning}
                  sx={{ mr: 1, color: ORANGE }}
                />
                <Typography mr={1}>{t('unsavedChanges')}</Typography>
              </>
            )}

            <Button
              type="submit"
              variant="contained"
              color="dark"
              disabled={
                !formState.isDirty || Object.keys(formState.errors).length > 0
              }
            >
              {t('save')}
            </Button>
          </Box>
        </form>
      </FormProvider>

      <RoundModal
        round={currentRound}
        isOpen={!!currentRound}
        onSave={updateRound}
        onClose={() => setCurrentRound(null)}
        isEdit={!!currentRound}
      />

      <LoadingOverlay loading={isLoading} />
    </>
  );
};

export default AIMatchSettings;
